import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Target, Trophy, History, Clock } from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface SpinnerResult {
  id: number;
  label: string;
  entries: number;
  probability: number;
  color: string;
  isActive: boolean;
}

interface SpinnerSettings {
  enabled: boolean;
  title: string;
  description: string;
}

interface SpinnerPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SpinnerPopup({ isOpen, onClose }: SpinnerPopupProps) {
  const { toast } = useToast();
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [lastWin, setLastWin] = useState<any>(null);
  const [showResult, setShowResult] = useState(false);

  // Fetch spinner data
  const { data: canSpin = false } = useQuery<boolean>({
    queryKey: ['/api/spinner/can-spin'],
    enabled: isOpen,
  });

  const { data: spinnerResults = [] } = useQuery<SpinnerResult[]>({
    queryKey: ['/api/spinner/results'],
    enabled: isOpen,
  });

  const { data: spinnerSettings } = useQuery<SpinnerSettings>({
    queryKey: ['/api/spinner/settings'],
    enabled: isOpen,
  });

  const { data: spinnerHistory = [] } = useQuery<any[]>({
    queryKey: ['/api/spinner/history'],
    enabled: isOpen,
  });

  // Spin mutation
  const spinMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/spinner/spin');
      return response.json();
    },
    onSuccess: (data) => {
      setLastWin(data);
      setShowResult(true);
      queryClient.invalidateQueries({ queryKey: ['/api/spinner/can-spin'] });
      queryClient.invalidateQueries({ queryKey: ['/api/spinner/history'] });
      toast({
        title: "Spin Complete!",
        description: `You won: ${data.result.label}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Spin Failed",
        description: error?.message || "Unable to spin the wheel",
        variant: "destructive",
      });
      setIsSpinning(false);
    },
  });

  const handleSpin = () => {
    if (!canSpin || isSpinning) return;
    
    setIsSpinning(true);
    setShowResult(false);
    
    // Calculate spin rotation (3-5 full rotations plus random position)
    const baseRotation = 1080 + Math.random() * 720; // 3-5 rotations
    const finalRotation = rotationDegrees + baseRotation;
    
    setRotationDegrees(finalRotation);
    
    // Complete spin after animation
    setTimeout(() => {
      spinMutation.mutate();
      setIsSpinning(false);
    }, 3000);
  };

  const totalEntriesWon = spinnerHistory.reduce((sum: number, entry: any) => sum + entry.entriesWon, 0);

  if (!spinnerSettings?.enabled) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>{spinnerSettings?.title || 'Spin to Win Entries!'}</DialogTitle>
          <DialogDescription>
            {spinnerSettings?.description || 'Spin the wheel daily for a chance to win competition entries!'}
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {spinnerSettings?.title || 'Spin to Win Entries!'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {spinnerSettings?.description || 'Spin the wheel daily for a chance to win competition entries!'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="p-6 space-y-6">
            {/* Spinner Wheel */}
            <div className="flex flex-col items-center space-y-6">
              <div className="relative">
                {/* Wheel */}
                <div
                  className="relative w-64 h-64 rounded-full border-8 border-gray-200 dark:border-gray-700 overflow-hidden shadow-2xl"
                  style={{
                    background: `conic-gradient(${spinnerResults
                      .map((result, index) => {
                        const startAngle = (index / spinnerResults.length) * 360;
                        const endAngle = ((index + 1) / spinnerResults.length) * 360;
                        return `${result.color} ${startAngle}deg ${endAngle}deg`;
                      })
                      .join(', ')})`,
                    transform: `rotate(${rotationDegrees}deg)`,
                    transition: isSpinning ? 'transform 3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                  }}
                >
                  {/* Segments with labels */}
                  {spinnerResults.map((result, index) => {
                    const angle = (360 / spinnerResults.length) * index + (360 / spinnerResults.length) / 2;
                    const radius = 100;
                    const x = Math.cos((angle - 90) * (Math.PI / 180)) * radius;
                    const y = Math.sin((angle - 90) * (Math.PI / 180)) * radius;
                    
                    return (
                      <div
                        key={result.id}
                        className="absolute text-white font-bold text-xs pointer-events-none"
                        style={{
                          left: `calc(50% + ${x * 0.6}px)`,
                          top: `calc(50% + ${y * 0.6}px)`,
                          transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                          textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                        }}
                      >
                        <div style={{ transform: `rotate(-${angle}deg)` }}>
                          {result.entries > 0 ? `${result.entries}` : 'Try Again'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pointer */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="w-8 h-8 bg-yellow-400 rounded-full border-2 border-yellow-600 flex items-center justify-center shadow-lg">
                    <div className="w-3 h-3 bg-yellow-600 rounded-full"></div>
                  </div>
                  <div className="w-0 h-0 border-l-4 border-r-4 border-t-8 border-transparent border-t-yellow-400 mx-auto"></div>
                </div>
              </div>

              {/* Spin Button */}
              <Button
                onClick={handleSpin}
                disabled={!canSpin || isSpinning}
                size="lg"
                className="px-8 py-4 text-lg font-bold bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-950 hover:to-blue-900 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isSpinning ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Spinning...
                  </>
                ) : !canSpin ? (
                  <>
                    <Clock className="h-5 w-5 mr-2" />
                    Come Back Tomorrow
                  </>
                ) : (
                  <>
                    <Target className="h-5 w-5 mr-2" />
                    Spin to Win!
                  </>
                )}
              </Button>
            </div>

            {/* Last Win Result */}
            <AnimatePresence>
              {showResult && lastWin && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="text-center space-y-2"
                >
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {lastWin.result.label}
                  </div>
                  {lastWin.result.entries > 0 && (
                    <Badge variant="secondary" className="text-lg px-4 py-2">
                      +{lastWin.result.entries} entries
                    </Badge>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Stats */}
            <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Your Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-900">{totalEntriesWon}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Entries Won</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{spinnerHistory.length}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Spins</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {spinnerHistory.length > 0 ? Math.round((spinnerHistory.filter((h: any) => h.entriesWon > 0).length / spinnerHistory.length) * 100) : 0}%
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Win Rate</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent History */}
            {spinnerHistory.length > 0 && (
              <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Recent Spins
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {spinnerHistory.slice(0, 5).map((entry: any) => (
                      <div key={entry.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </span>
                        <Badge variant={entry.entriesWon > 0 ? "default" : "secondary"}>
                          {entry.entriesWon > 0 ? `+${entry.entriesWon} entries` : 'No win'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}